import { zodResolver } from "@hookform/resolvers/zod";
import { router, usePage } from "@inertiajs/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ResponsiveDialogButton } from "@/components/shared/responsive-dialog";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type { ServiceCredential } from "@/types/admin-portal/service-credential";
import type { GlobalPageProps, ResponsiveDialogMode } from "@/types/globals";

const SERVICE_TYPES = ["internal", "external"] as const;

const formSchema = z.object({
	serviceName: z.string().min(1, { message: "required" }),
	serviceType: z.enum(SERVICE_TYPES),
	description: z.string().optional(),
	clientId: z.string().optional(),
	clientName: z.string().optional(),
	isActive: z.boolean(),
	maxRequestsPerMinute: z
		.string()
		.optional()
		.refine(
			(value) => {
				if (!value || value.trim() === "") return true;
				const parsed = Number(value);
				return Number.isInteger(parsed) && parsed > 0;
			},
			{ message: "invalid" },
		),
	ipWhitelist: z.string().optional(),
	allowedEndpoints: z.string().optional(),
	clientMetadata: z
		.string()
		.optional()
		.refine((value) => isValidJsonObject(value), { message: "invalid" }),
});

type FormValues = z.infer<typeof formSchema>;

type Props = {
	credential?: ServiceCredential | null;
	forceMode?: ResponsiveDialogMode;
	handleOpenChange?: (open: boolean) => void;
};

export default function ServiceCredentialFormFields({
	credential,
	forceMode,
	handleOpenChange,
}: Props) {
	const { props: globalProps } = usePage<
		GlobalPageProps & { errors?: Record<string, string[]> }
	>();
	const { t } = useTranslation("service-credentials");
	const { toast } = useToast();
	const isMobile = useIsMobile();
	const isEditMode = Boolean(credential);
	const [isLoading, setIsLoading] = useState(false);

	const baseRoute =
		globalProps.adminPortal.router.adminPortal.serviceCredentials?.index;

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			serviceName: "",
			serviceType: "internal",
			description: "",
			clientId: "",
			clientName: "",
			isActive: true,
			maxRequestsPerMinute: "",
			ipWhitelist: "",
			allowedEndpoints: "",
			clientMetadata: "",
		},
		mode: "onBlur",
	});

	useEffect(() => {
		if (!credential) return;
		form.reset(mapToFormValues(credential));
	}, [credential, form]);

	// Server-side validation errors are attached to their fields so the message
	// shows under the offending input, mirroring the voucher form.
	useEffect(() => {
		if (!globalProps?.errors) return;
		for (const [field, messages] of Object.entries(globalProps.errors)) {
			if (field === "fullMessages") continue;
			form.setError(field as keyof FormValues, {
				type: "server",
				message: Array.isArray(messages) ? messages.join(", ") : messages,
			});
		}
	}, [globalProps?.errors, form]);

	const buttonProps = useMemo(
		() => ({
			isLoading,
			forceMode,
			submitText: isEditMode ? t("button.edit") : t("button.add_data"),
		}),
		[isLoading, forceMode, isEditMode, t],
	);

	const onSubmit = useCallback(
		(values: FormValues) => {
			if (!baseRoute) return;

			const routeURL = isEditMode
				? `${baseRoute}/${credential?.id}`
				: baseRoute;
			// `clientMetadata` is sent as the raw JSON string; the server parses it
			// (and reports a malformed document as a field error).
			const payload = deepTransformKeysToSnakeCase({
				serviceCredential: {
					serviceName: values.serviceName.trim(),
					serviceType: values.serviceType,
					description: values.description?.trim() || null,
					clientId: values.clientId?.trim() || null,
					clientName: values.clientName?.trim() || null,
					isActive: values.isActive,
					maxRequestsPerMinute: parseOptionalInt(values.maxRequestsPerMinute),
					ipWhitelist: linesToArray(values.ipWhitelist),
					allowedEndpoints: linesToArray(values.allowedEndpoints),
					clientMetadata: values.clientMetadata?.trim() || null,
				},
			});

			const requestOptions = {
				preserveScroll: true,
				preserveState: true,
				only: ["adminPortal", "flash", "errors", "serviceCredentials"],
				onStart: () => setIsLoading(true),
				onSuccess: () => {
					form.reset();
					handleOpenChange?.(false);
					if (!isEditMode) {
						toast({ description: t("toast.created") });
					}
				},
				onError: () => {
					toast({
						description: t("toast.submit_error"),
						variant: "destructive",
					});
				},
				onFinish: () => setTimeout(() => setIsLoading(false), 300),
			};

			if (isEditMode) {
				router.put(routeURL, payload, requestOptions);
				return;
			}
			router.post(routeURL, payload, requestOptions);
		},
		[baseRoute, isEditMode, credential?.id, form, handleOpenChange, toast, t],
	);

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)}>
				<div
					className={cn(
						"space-y-6 px-1",
						isMobile ? "max-h-[75dvh] overflow-y-auto pb-4" : "",
					)}
				>
					<div className="grid gap-4 md:grid-cols-2">
						<FormField
							control={form.control}
							name="serviceName"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t("fields.service_name.label")}</FormLabel>
									<FormControl>
										<Input
											{...field}
											placeholder={t("fields.service_name.placeholder")}
											className="bg-input"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="serviceType"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t("fields.service_type.label")}</FormLabel>
									<FormControl>
										<Select onValueChange={field.onChange} value={field.value}>
											<SelectTrigger className="bg-input">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{SERVICE_TYPES.map((type) => (
													<SelectItem key={type} value={type}>
														{t(`service_type.${type}`)}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="clientId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t("fields.client_id.label")}{" "}
										<OptionalHint text={t("fields.client_id.optional")} />
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											placeholder={t("fields.client_id.placeholder")}
											className="bg-input"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="clientName"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t("fields.client_name.label")}{" "}
										<OptionalHint text={t("fields.client_name.optional")} />
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											placeholder={t("fields.client_name.placeholder")}
											className="bg-input"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="maxRequestsPerMinute"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t("fields.max_requests_per_minute.label")}{" "}
										<OptionalHint
											text={t("fields.max_requests_per_minute.optional")}
										/>
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											type="number"
											min={1}
											step="1"
											placeholder={t(
												"fields.max_requests_per_minute.placeholder",
											)}
											className="bg-input appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
										/>
									</FormControl>
									{form.formState.errors.maxRequestsPerMinute?.message ===
									"invalid" ? (
										<p className="text-sm font-medium text-destructive">
											{t("fields.max_requests_per_minute.invalid")}
										</p>
									) : null}
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="isActive"
							render={({ field }) => (
								<FormItem className="flex flex-col gap-2">
									<div className="flex items-center justify-between rounded-md border px-3 py-2">
										<div>
											<FormLabel className="text-base">
												{t("fields.is_active.label")}
											</FormLabel>
											<p className="text-sm text-muted-foreground">
												{t("fields.is_active.description")}
											</p>
										</div>
										<FormControl>
											<Switch
												checked={field.value}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<FormField
						control={form.control}
						name="description"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t("fields.description.label")}{" "}
									<OptionalHint text={t("fields.description.optional")} />
								</FormLabel>
								<FormControl>
									<Textarea
										{...field}
										placeholder={t("fields.description.placeholder")}
										className="bg-input"
										rows={2}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="grid gap-4 md:grid-cols-2">
						<FormField
							control={form.control}
							name="ipWhitelist"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t("fields.ip_whitelist.label")}{" "}
										<OptionalHint text={t("fields.ip_whitelist.optional")} />
									</FormLabel>
									<FormControl>
										<Textarea
											{...field}
											placeholder={t("fields.ip_whitelist.placeholder")}
											className="bg-input font-mono text-xs"
											rows={3}
										/>
									</FormControl>
									<FormDescription>
										{t("fields.ip_whitelist.description")}
									</FormDescription>
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="allowedEndpoints"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t("fields.allowed_endpoints.label")}{" "}
										<OptionalHint
											text={t("fields.allowed_endpoints.optional")}
										/>
									</FormLabel>
									<FormControl>
										<Textarea
											{...field}
											placeholder={t("fields.allowed_endpoints.placeholder")}
											className="bg-input font-mono text-xs"
											rows={3}
										/>
									</FormControl>
									<FormDescription>
										{t("fields.allowed_endpoints.description")}
									</FormDescription>
								</FormItem>
							)}
						/>
					</div>

					<FormField
						control={form.control}
						name="clientMetadata"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{t("fields.client_metadata.label")}{" "}
									<OptionalHint text={t("fields.client_metadata.optional")} />
								</FormLabel>
								<FormControl>
									<Textarea
										{...field}
										placeholder={t("fields.client_metadata.placeholder")}
										className="bg-input font-mono text-xs"
										rows={4}
									/>
								</FormControl>
								<FormDescription>
									{t("fields.client_metadata.description")}
								</FormDescription>
								{form.formState.errors.clientMetadata?.message === "invalid" ? (
									<p className="text-sm font-medium text-destructive">
										{t("fields.client_metadata.invalid")}
									</p>
								) : null}
							</FormItem>
						)}
					/>
				</div>

				<ResponsiveDialogButton {...buttonProps} />
			</form>
		</Form>
	);
}

function OptionalHint({ text }: { text: string }) {
	return <span className="text-sm italic font-light">{text}</span>;
}

function isValidJsonObject(value?: string): boolean {
	if (!value || value.trim() === "") return true;
	try {
		const parsed = JSON.parse(value);
		return (
			parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)
		);
	} catch {
		return false;
	}
}

function parseOptionalInt(value?: string): number | null {
	if (!value || value.trim() === "") return null;
	const parsed = Number(value);
	return Number.isInteger(parsed) ? parsed : null;
}

// Textarea holds one entry per line; empty means NULL (allow all) server-side.
function linesToArray(value?: string): string[] | null {
	if (!value) return null;
	const items = value
		.split("\n")
		.map((line) => line.trim())
		.filter((line) => line.length > 0);
	return items.length ? items : null;
}

function mapToFormValues(credential: ServiceCredential): FormValues {
	return {
		serviceName: credential.serviceName ?? "",
		serviceType: credential.serviceType,
		description: credential.description ?? "",
		clientId: credential.clientId ?? "",
		clientName: credential.clientName ?? "",
		isActive: Boolean(credential.isActive),
		maxRequestsPerMinute:
			credential.maxRequestsPerMinute == null
				? ""
				: String(credential.maxRequestsPerMinute),
		ipWhitelist: (credential.ipWhitelist ?? []).join("\n"),
		allowedEndpoints: (credential.allowedEndpoints ?? []).join("\n"),
		clientMetadata: credential.clientMetadata
			? JSON.stringify(credential.clientMetadata, null, 2)
			: "",
	};
}
